import React from 'react';
import {
    MousePointer,
    Clock,
    CreditCard,
    RotateCcw,
    Info,
    X,
    Play,
    StopCircle,
} from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface NavigationLegendProps {
    isOpen: boolean;
    onClose: () => void;
}

const CONNECTION_TYPES = [
    {
        trigger: 'start',
        name: 'Experience Start',
        color: '#10B981',
        icon: Play,
        description: 'Entry point of the experience',
        style: 'start-indicator',
    },
    {
        trigger: 'exit',
        name: 'Experience Exit',
        color: '#EF4444',
        icon: StopCircle,
        description: 'Exit point (reset/share actions)',
        style: 'exit-indicator',
    },
    {
        trigger: 'button',
        name: 'Button Action',
        color: '#3B82F6',
        icon: MousePointer,
        description: 'User clicks a button to navigate',
        style: 'solid',
    },
    {
        trigger: 'automatic',
        name: 'Automatic',
        color: '#10B981',
        icon: RotateCcw,
        description: 'Automatic navigation after capture/processing',
        style: 'solid thick',
    },
    {
        trigger: 'payment',
        name: 'Payment Success',
        color: '#F59E0B',
        icon: CreditCard,
        description: 'Navigation after successful payment',
        style: 'dashed',
    },
    {
        trigger: 'timeout',
        name: 'Timeout',
        color: '#EF4444',
        icon: Clock,
        description: 'Navigation after time delay',
        style: 'dashed long',
    },
];

export default function NavigationLegend({
    isOpen,
    onClose,
}: NavigationLegendProps) {
    if (!isOpen) return null;

    return (
        <div className="absolute bottom-4 left-4 z-20 bg-background rounded-lg shadow-lg border border-border w-80">
            <div className="p-4 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Info className="w-4 h-4 text-blue-500" />
                    <h3 className="text-sm font-semibold text-foreground">
                        Navigation Flow Legend
                    </h3>
                </div>
                <Button variant="ghost" size="icon-sm" onClick={onClose}>
                    <X className="w-4 h-4" />
                </Button>
            </div>

            <div className="p-4">
                <div className="space-y-3">
                    {CONNECTION_TYPES.map((type) => {
                        const IconComponent = type.icon;
                        return (
                            <div
                                key={type.trigger}
                                className="flex items-center gap-3"
                            >
                                <div className="flex items-center gap-2">
                                    <div
                                        className="p-1.5 rounded-lg"
                                        style={{
                                            backgroundColor: `${type.color}20`,
                                        }}
                                    >
                                        <IconComponent
                                            className="w-3 h-3"
                                            style={{ color: type.color }}
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {type.style === 'start-indicator' ? (
                                            <svg
                                                width="24"
                                                height="12"
                                                viewBox="0 0 24 12"
                                            >
                                                {/* Green circle outline */}
                                                <circle
                                                    cx="4"
                                                    cy="6"
                                                    r="3"
                                                    fill="none"
                                                    stroke={type.color}
                                                    strokeWidth="2"
                                                />
                                                {/* Horizontal line */}
                                                <line
                                                    x1="7"
                                                    y1="6"
                                                    x2="20"
                                                    y2="6"
                                                    stroke={type.color}
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                        ) : type.style === 'exit-indicator' ? (
                                            <svg
                                                width="24"
                                                height="12"
                                                viewBox="0 0 24 12"
                                            >
                                                {/* Horizontal line */}
                                                <line
                                                    x1="4"
                                                    y1="6"
                                                    x2="17"
                                                    y2="6"
                                                    stroke={type.color}
                                                    strokeWidth="2"
                                                />
                                                {/* Red circle outline */}
                                                <circle
                                                    cx="20"
                                                    cy="6"
                                                    r="3"
                                                    fill="none"
                                                    stroke={type.color}
                                                    strokeWidth="2"
                                                />
                                            </svg>
                                        ) : (
                                            <svg
                                                width="24"
                                                height="12"
                                                viewBox="0 0 24 12"
                                            >
                                                <line
                                                    x1="0"
                                                    y1="6"
                                                    x2="20"
                                                    y2="6"
                                                    stroke={type.color}
                                                    strokeWidth={
                                                        type.style.includes(
                                                            'thick',
                                                        )
                                                            ? 3
                                                            : 2
                                                    }
                                                    strokeDasharray={
                                                        type.style.includes(
                                                            'dashed',
                                                        )
                                                            ? type.style.includes(
                                                                  'long',
                                                              )
                                                                ? '10,5'
                                                                : '5,5'
                                                            : undefined
                                                    }
                                                    opacity={0.8}
                                                />
                                                <polygon
                                                    points="20,6 16,3 16,9"
                                                    fill={type.color}
                                                    opacity={0.8}
                                                />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-foreground">
                                        {type.name}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {type.description}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-4 pt-3 border-t border-border">
                    <div className="text-xs text-muted-foreground">
                        <strong>Navigation flows</strong> are automatically
                        detected from your screen definitions. Toggle visibility
                        with the route button in the toolbar.
                    </div>
                </div>
            </div>
        </div>
    );
}
